import { useEffect, useLayoutEffect, useMemo } from "react";
import { usePostHog } from "posthog-js/react";
import { AnimatePresence } from "framer-motion";
import { TopNav } from "../TopNav/TopNav";
import { BottomNav } from "../BottomNav/BottomNav";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { Match } from "../../pages/marketing/Match/Match";
import { Conversation } from "../../pages/marketing/Conversation/Conversation";
import { EventV2 } from "../../pages/marketing/EventV2/EventV2";
import { EventPreSpark } from "../../pages/marketing/EventPreSpark/EventPreSpark";

/**
 * Renders the Match screen as a modal overlay when the URL matches
 * `/event/:sparkId/match`. Mounted as a sibling to <Outlet /> so the originating
 * Events page (and the MatchFillWidget) stays mounted behind it. The
 * Match component owns its own mount/exit animation (variants).
 */
function MatchOverlay() {
  const match = useMatch("/event/:sparkId/match");
  return (
    <AnimatePresence>{match ? <Match key="match-modal" /> : null}</AnimatePresence>
  );
}

/**
 * Renders the Conversation screen as a modal overlay when the URL matches
 * `/matches/:userId`. Same overlay pattern as MatchOverlay — keeps the
 * Matches page mounted behind it so the back gesture feels instant.
 */
function ConversationOverlay() {
  const match = useMatch("/matches/:userId");
  return (
    <AnimatePresence>
      {match ? <Conversation key="conversation-modal" /> : null}
    </AnimatePresence>
  );
}

/**
 * Renders the EventV2 screen as a modal overlay when the URL matches
 * `/event/atlanta/:sparkId`. Same overlay pattern as MatchOverlay — keeps
 * the Events list mounted behind it for an instant back gesture.
 */
function EventOverlay() {
  const match = useMatch("/event/atlanta/:sparkId");
  return (
    <AnimatePresence>
      {match ? <EventV2 key="event-modal" /> : null}
    </AnimatePresence>
  );
}
/**
 * Renders an event's "Spark before you go" deck as a modal overlay when the
 * URL matches `/event/:sparkId/spark`. Same overlay pattern as MatchOverlay.
 */
function EventPreSparkOverlay() {
  const match = useMatch("/event/:sparkId/spark");
  return (
    <AnimatePresence>
      {match ? <EventPreSpark key="event-pre-spark-modal" /> : null}
    </AnimatePresence>
  );
}
import { Meta } from "../Meta/Meta";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { Box } from "@chakra-ui/react";
import { background } from "../../lib/useBackground";
import { useStoreData } from "../../lib/store/store";
import { apiReq } from "../../lib/apiReq";
import { syncUtmFromUrl } from "../../lib/utm";
import mobile from "is-mobile";
import { isLoggedInPage } from "../../lib/isLoggedInPage";
import { isLoggedIn } from "../../lib/isLoggedIn";
import { Footer } from "../Footer/Footer";
import { Capacitor } from "@capacitor/core";
import { HideNavProvider, useIsNavHidden } from "../../lib/useHideNav";
import { useRememberWorkspacePath } from "../../lib/useWorkspace";
import { DevAccountSwitcher } from "../DevAccountSwitcher/DevAccountSwitcher";
import { SettingsOverlay } from "../../pages/marketing/Settings/SettingsOverlay";

dayjs.extend(localizedFormat);

export function Page() {
  return (
    <HideNavProvider>
      <PageInner />
    </HideNavProvider>
  );
}

function PageInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const storeData = useStoreData();
  const { hash } = useLocation();
  const { hidden: navHidden, footerHidden } = useIsNavHidden();
  useRememberWorkspacePath();

  const isMobile = useMemo(() => mobile(), []);
  const loggedIn = useMemo(() => isLoggedIn(storeData), [storeData]);

  useEffect(() => {
    if (isLoggedInPage(location.pathname) && !storeData.loading && !loggedIn) {
      navigate(
        `/sign-in?r=${encodeURIComponent(location.pathname + location.search)}`,
        {
          replace: true,
        },
      );
    }
  }, [location.pathname, location.search, loggedIn, navigate, storeData]);

  useEffect(() => {
    document.getElementById("scroll-container")?.scrollTo(0, 0);
    syncUtmFromUrl(location.search, apiReq.headers);
    // Both trackers are initialized before the app mounts (posthog in
    // index.tsx with auto-capture disabled, the Meta pixel in index.html), so
    // this effect is the single source of pageviews for the initial load and
    // every client-side navigation.
    posthog.capture("$pageview");
    if (window.fbq) {
      window.fbq("track", "PageView");
    }
    // Depend on pathname+search only: a hash- or state-only navigation isn't a
    // new page and must not re-fire the pageview / PageView events.
  }, [location.pathname, location.search, posthog]);
  useLayoutEffect(() => {
    const scrollContainer = document.getElementById("scroll-container");
    const targets = [document.documentElement, document.body];
    const prev = targets.map((el) => el.style.background);
    const prevScroll = scrollContainer?.style.background ?? "";

    const apply = (value: string) => {
      targets.forEach((el) => {
        el.style.background = value;
      });
      if (scrollContainer) {
        scrollContainer.style.background = value ? "transparent" : prevScroll;
      }
    };

    apply(background.val);
    const onUpdate = (e: Event) =>
      apply((e as CustomEvent<{ value: string }>).detail.value);
    background.addEventListener("update", onUpdate);

    return () => {
      background.removeEventListener("update", onUpdate);
      targets.forEach((el, i) => {
        el.style.background = prev[i];
      });
      if (scrollContainer) scrollContainer.style.background = prevScroll;
    };
  }, []);

  useLayoutEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const el = document.getElementById(id);
    setTimeout(() => el?.scrollIntoView({ behavior: "smooth" }), 20);
  }, [hash]);

  const showTopNav = !navHidden;
  const showBottomNav = loggedIn && isMobile && !navHidden;
  // Asked for by the page itself, not inferred from the nav being hidden: the
  // claim and invite flows hide the nav but still take payment, so they keep
  // the footer's refund and privacy links.
  const showFooter =
    !Capacitor.isNativePlatform() &&
    !footerHidden &&
    (!isMobile || !loggedIn || location.pathname === "/");

  return (
    <Box
      as="main"
      paddingTop="env(safe-area-inset-top)"
      paddingLeft="env(safe-area-inset-left)"
      paddingRight="env(safe-area-inset-right)"
      className="page"
      display="flex"
      flexDirection="column"
      height="100dvh"
      overflow="hidden"
    >
      <Meta>
        <meta name="description" content="Figure out your dating style" />
        <meta
          name="viewport"
          content="viewport-fit=cover, width=device-width, initial-scale=1.0"
        />
        <meta property="og:title" content="" />
        <meta property="og:type" content="" />
        <meta property="og:url" content="" />
        <meta property="og:image" content="" />

        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
      </Meta>
      {showTopNav && <TopNav />}
      <Box
        id="scroll-container"
        flex={1}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        paddingBottom={`calc(env(safe-area-inset-bottom) + ${
          showBottomNav ? "4.5rem" : "0"
        })`}
      >
        <Box flex={1} display="flex" flexDirection="column">
          {/*
            `/event/:sparkId/match` reuses <Events /> as its route element so
            the originating page (and the MatchFillWidget) stays mounted.
            The Match screen is rendered as a modal overlay via
            <MatchOverlay /> — a sibling to <Outlet />.
          */}
          <Outlet />
          <MatchOverlay />
          <ConversationOverlay />
          <EventOverlay />
          <EventPreSparkOverlay />
          <SettingsOverlay />
        </Box>
        {showFooter && <Footer />}
      </Box>
      {showBottomNav && <BottomNav />}
      {import.meta.env.DEV && <DevAccountSwitcher />}
    </Box>
  );
}
